import React, { useMemo } from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import type { AccommodationListingDTO } from '@shared/types/Accommodations.types';
import { SkeletonText } from '@shared/components/Skeleton';
import { StarRating } from '@apps/stays/components/Ratings';
import type { PointOfInterest } from './TravelLogistics';
import { TravelLogistics } from './TravelLogistics';
import type { Maybe } from '@graphql/generated-base';

type HeaderBlockProps = Readonly<{
  listing: AccommodationListingDTO;
  pointOfInterest: Maybe<PointOfInterest>;
  isMobileOrTablet: boolean;
  redirectUrl?: string;
  // Whether the name links to the PDP. Defaults to `!isSoldOut`; treatment enables it for sold-out tiles.
  isLinkEnabled?: boolean;
}>;

const NameAndStarRatingPanel = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      gap={5}
      flexWrap={'wrap'}
      alignItems={'center'}
    >
      {children}
    </Flex>
  );
};

const HeaderBlock = ({ listing, pointOfInterest, isMobileOrTablet, redirectUrl, isLinkEnabled }: HeaderBlockProps) => {
  const addressText = useMemo(() => {
    if (listing.address) {
      return [listing.address.street, listing.address.city].filter(Boolean).join(', ');
    }

    return null;
  }, [listing]);
  const isSoldOut = listing.type === 'standardHotel' && listing.isSoldOutForSelectedDates;
  const linkEnabled = isLinkEnabled ?? !isSoldOut;

  const listingNameProps = {
    overflow: 'hidden',
    display: '-webkit-box',
    typographyVariant: 'hed2',
    style: {
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
    },
  } as const;

  if (!isMobileOrTablet) {
    return (
      <Flex
        flexDirection="column"
        rowGap={2}
      >
        {typeof listing.starRating === 'number' && listing.starRating > 0 && <StarRating rating={listing.starRating} />}
        <TextV2
          {...listingNameProps}
          _hover={{ textDecoration: linkEnabled && redirectUrl ? 'underline' : 'none' }}
        >
          {listing.name}
        </TextV2>
        {listing.address && (
          <TextV2
            typographyVariant="label2"
            color="mono10"
          >
            {addressText}
          </TextV2>
        )}
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection="column"
      rowGap={3}
    >
      <NameAndStarRatingPanel>
        <TextV2
          {...listingNameProps}
          maxWidth={pxToRem(260)}
        >
          {listing.name}
        </TextV2>
        {typeof listing.starRating === 'number' && listing.starRating > 0 && <StarRating rating={listing.starRating} />}
      </NameAndStarRatingPanel>
      {pointOfInterest && (
        <TravelLogistics
          useShortWording={true}
          pointOfInterest={pointOfInterest}
          listing={listing}
        />
      )}
    </Flex>
  );
};

HeaderBlock.Skeleton = ({ isMobileOrTablet }: { isMobileOrTablet: boolean }) => {
  if (!isMobileOrTablet) {
    return (
      <Flex
        flexDirection="column"
        rowGap={2}
      >
        <StarRating.Skeleton />
        <Flex
          flexDirection="column"
          rowGap={3}
        >
          <SkeletonText
            rows={1}
            skeletonHeight={20}
            width={175}
          />
          <SkeletonText
            rows={1}
            skeletonHeight={20}
            width={80}
          />
        </Flex>
        <SkeletonText
          rows={1}
          skeletonHeight={18}
          width={160}
        />
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection="column"
      rowGap={3}
    >
      <NameAndStarRatingPanel>
        <SkeletonText
          rows={1}
          skeletonHeight={20}
          width={175}
        />
        <StarRating.Skeleton />
      </NameAndStarRatingPanel>
      <TravelLogistics.Skeleton />
    </Flex>
  );
};

export { HeaderBlock };
